import React from 'react';
import classNames from 'classnames';
import _isNil from 'lodash/isNil';
import _round from 'lodash/round';
import Typography from '@lib/components/Typography/Typography';
import { PlanState } from '@lib/enums/plan';
import { getPlanState } from '@lib/utils/accessesManagement';
import styles from './PercentageBarCell.module.scss';

interface Props {
  // Bars that measure a share of a whole (e.g. one user's slice of the total AI
  // spend) must stay neutral: a large share is not a warning, unlike consuming
  // a limit.
  neutral?: boolean;
  percentage: number | null | undefined;
  precision?: number;
}

export default function PercentageBarCell({
  neutral = false,
  percentage,
  precision = 0,
}: Props) {
  if (_isNil(percentage)) {
    return <Typography variant="caption">-</Typography>;
  }
  const roundedPercentage = _round(percentage, precision);
  const planState = neutral ? PlanState.None : getPlanState(roundedPercentage);
  // The backend can report over 100% (e.g. a budget overrun) — clamp only
  // the bar's flex-grow values so an out-of-range input never produces a
  // negative (invalid) flex-grow; the label still shows the real number.
  const clampedPercentage = Math.min(Math.max(roundedPercentage, 0), 100);
  return (
    <div className={styles.root}>
      <div className={styles.track}>
        <div
          className={classNames(styles.fill, {
            [styles.fillWarning]: planState === PlanState.Warning,
            [styles.fillAttention]: planState === PlanState.Attention,
          })}
          style={{ flex: `${clampedPercentage} 0 0` }}
        />
        <div
          className={styles.empty}
          style={{ flex: `${100 - clampedPercentage} 0 0` }}
        />
      </div>
      <Typography variant="caption" className={styles.label}>
        {`${roundedPercentage}%`}
      </Typography>
    </div>
  );
}
